import { AlertTriangle, Check, ShieldAlert, X } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/cn';
import { devicesInScope, type GrantPreview, type HierarchySnapshot } from './effective-permissions';

/*
  The confirmation surface for a role assignment: what this grant actually does, before anyone
  presses the button.

  It answers three questions, in this order, because that is the order they matter:

  1. WHERE does it reach. A permission list without a scope is meaningless, and the honest way to
     say "Contoso Freight" is to also say how many enrolled devices that is right now. The count
     comes from the devices this account can see, so it is a floor rather than a total, and the
     copy says so instead of pretending to be authoritative.

  2. WHICH permissions, with the dangerous ones marked. Dangerous is not a UI opinion: it is
     stored on the permissions row and it drives three separate behaviours, one of which is this
     mark (docs/07-identity-rbac.md I.2). The others are a required step-up MFA assertion in the
     API and a minimum audit severity of notice, and both are listed here so nobody reads the red
     badge as decoration.

  3. WHAT CANNOT be granted. When the grantor does not hold a permission at this scope, the role
     cannot be granted at all, and this panel names every missing permission rather than saying
     "not permitted". The alternative is a technician retrying the same refused grant five times
     because nothing told them which of twenty permissions was the problem.

  No colour carries information on its own here. Grantable and blocked permissions differ by
  glyph (a tick against a cross) and by strikethrough as well as by tone, and the dangerous mark
  carries the word "dangerous" in its title.
*/

export interface GrantPreviewPanelProps {
  readonly preview: GrantPreview;
  readonly hierarchy: HierarchySnapshot;
  readonly scopeLabel: string;
  /** Name of the person being granted, for the copy. */
  readonly subject: string;
}

export function GrantPreviewPanel({
  preview,
  hierarchy,
  scopeLabel,
  subject,
}: GrantPreviewPanelProps): React.JSX.Element {
  const reached = devicesInScope(preview.scope, hierarchy);
  const blockedCount = preview.blocked.length;

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-[var(--radius-control)] border border-border bg-surface-sunken p-3">
        <h4 className="text-xs font-semibold tracking-wide text-fg-muted uppercase">
          What this grant does
        </h4>
        <p className="mt-1.5 text-sm text-fg">
          {subject} would hold <span className="font-semibold">{preview.role.name}</span> on{' '}
          <span className="font-semibold">{scopeLabel}</span>
          {preview.scope.kind === 'tenant' ? '' : ' and everything below it'}.
        </p>
        <p className="mt-1 text-xs text-fg-muted">
          That currently reaches{' '}
          <span className="font-semibold text-fg tabular-nums">{formatNumber(reached.length)}</span>{' '}
          {reached.length === 1 ? 'enrolled device' : 'enrolled devices'}, counted from the devices
          this account can see, and it will cover anything added under that node later.
        </p>
      </div>

      <div>
        <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold tracking-wide text-fg-muted uppercase">
          Effective permissions
          <span className="font-normal normal-case">
            ({formatNumber(preview.entries.length)} total
            {preview.dangerousCount > 0
              ? `, ${formatNumber(preview.dangerousCount)} dangerous`
              : ''}
            )
          </span>
        </h4>
        <ul className="grid gap-1 sm:grid-cols-2">
          {preview.entries.map((entry) => (
            <li
              key={entry.permission}
              className={cn(
                'flex items-center gap-1.5 rounded border px-1.5 py-1 text-xs',
                entry.grantable
                  ? entry.dangerous
                    ? 'border-[var(--color-danger)]/35 bg-[var(--color-danger-soft)]'
                    : 'border-border bg-surface'
                  : 'border-dashed border-border-strong bg-surface-sunken',
              )}
            >
              {entry.grantable ? (
                <Check
                  className="size-3 shrink-0 text-[var(--color-success)]"
                  aria-hidden="true"
                />
              ) : (
                <X className="size-3 shrink-0 text-fg-subtle" aria-hidden="true" />
              )}
              <span
                className={cn(
                  'font-mono',
                  entry.grantable
                    ? entry.dangerous
                      ? 'text-[var(--color-danger)]'
                      : 'text-fg'
                    : 'text-fg-subtle line-through',
                )}
              >
                {entry.permission}
              </span>
              {entry.dangerous ? (
                <ShieldAlert
                  className="size-3 shrink-0 text-[var(--color-danger)]"
                  aria-hidden="true"
                  aria-label="dangerous permission"
                />
              ) : null}
              {entry.grantable ? null : (
                <span className="sr-only">, cannot be granted by you at this scope</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {preview.dangerousCount > 0 && blockedCount === 0 ? (
        <Alert
          tone="danger"
          title={`${formatNumber(preview.dangerousCount)} of these are marked dangerous`}
        >
          <p>
            A dangerous permission does three things beyond granting access: the API requires a
            fresh MFA assertion from the last five minutes before the action runs, the audit entry
            is recorded at severity notice or higher, and the console asks for a second
            confirmation. Granting one is not the same as granting a read permission.
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-1">
            {preview.entries
              .filter((entry) => entry.dangerous)
              .map((entry) => (
                <Badge key={entry.permission} tone="danger">
                  <span className="font-mono text-[11px]">{entry.permission}</span>
                </Badge>
              ))}
          </p>
        </Alert>
      ) : null}

      {blockedCount > 0 ? (
        <Alert
          tone="warning"
          liveness="polite"
          title="You cannot grant this role at this scope"
        >
          <p className="flex items-start gap-1.5">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>
              A user cannot grant a permission they do not hold themselves at the scope in
              question. {formatNumber(blockedCount)} of the permissions in{' '}
              {preview.role.name}{' '}
              {blockedCount === 1 ? 'is one you do not hold' : 'are ones you do not hold'} on{' '}
              {scopeLabel}:
            </span>
          </p>
          <p className="mt-1.5 flex flex-wrap gap-1">
            {preview.blocked.map((permission) => (
              <Badge key={permission} tone="neutral">
                <span className="font-mono text-[11px]">{permission}</span>
              </Badge>
            ))}
          </p>
          <p className="mt-1.5">
            Choose a role that does not include them, choose a narrower scope where you do hold
            them, or ask someone who holds them to make the grant. The API enforces this
            independently, so this is an explanation of a refusal rather than the refusal itself.
          </p>
        </Alert>
      ) : null}
    </div>
  );
}
